import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowLeft, Code2, Server, Shield, Brain, Megaphone, Home, FolderArchive } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useI18n } from "../i18n";
import { SERVICE_SLUGS, serviceDetails, ui } from "../data/serviceDetails";

const ICONS = {
    dev: Code2,
    it: Server,
    sec: Shield,
    ai: Brain,
    mkt: Megaphone,
    iot: Home,
    doc: FolderArchive,
};

export default function ServicesOverview() {
    const { lang, dict } = useI18n();
    const u = ui[lang];
    const details = serviceDetails[lang];
    const cats = dict.services.cats;

    return (
        <div data-testid="services-overview-page" className="bg-ink-950 text-white">
            <Navbar />
            <main>
                {/* Hero */}
                <section className="relative overflow-hidden pt-32 md:pt-40 pb-16 md:pb-24">
                    <div className="pointer-events-none absolute inset-0 grid-bg opacity-30 [mask-image:linear-gradient(to_bottom,#000,transparent_85%)]" />
                    <div className="pointer-events-none absolute -top-40 left-1/2 -translate-x-1/2 h-[36rem] w-[80%] rounded-full bg-brand/15 blur-[140px]" />
                    <div className="container-page relative">
                        <Link
                            to="/"
                            data-testid="services-back-home"
                            className="inline-flex items-center gap-1.5 text-xs text-white/55 hover:text-white transition-colors"
                        >
                            <ArrowLeft className="h-3.5 w-3.5" />
                            {u.backHome}
                        </Link>
                        <div className="mt-6 max-w-3xl">
                            <span className="eyebrow">{u.servicesEyebrow}</span>
                            <h1 className="mt-5 text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tighter whitespace-pre-line leading-[1.05]">
                                {u.servicesTitle}
                            </h1>
                            <p className="mt-6 text-white/60 text-base md:text-lg max-w-2xl">{u.servicesSubtitle}</p>
                        </div>
                    </div>
                </section>

                {/* Grid of services */}
                <section className="pb-24 md:pb-32">
                    <div className="container-page grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                        {cats.map((cat, idx) => {
                            const Icon = ICONS[cat.key] || Code2;
                            const slug = SERVICE_SLUGS[cat.key];
                            const detail = details[cat.key];
                            return (
                                <motion.div
                                    key={cat.key}
                                    initial={{ opacity: 0, y: 16 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, margin: "-80px" }}
                                    transition={{ duration: 0.4, delay: idx * 0.04 }}
                                >
                                    <Link
                                        to={`/servicios/${slug}`}
                                        data-testid={`overview-service-${cat.key}`}
                                        className="group relative card-surface p-7 overflow-hidden block h-full"
                                    >
                                        <span className="absolute top-0 left-6 right-6 h-px bg-gradient-to-r from-transparent via-brand to-transparent opacity-0 group-hover:opacity-80 transition-opacity duration-500" />
                                        <span className="absolute -top-24 -right-24 h-48 w-48 rounded-full bg-brand/20 blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
                                        <div className="relative z-10 flex items-start justify-between">
                                            <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl border border-white/10 bg-white/[0.03] text-brand-glow group-hover:border-brand/40 transition-colors">
                                                <Icon className="h-5 w-5" />
                                            </div>
                                            <ArrowUpRight className="h-4 w-4 text-white/30 group-hover:text-white group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all" />
                                        </div>
                                        <h3 className="relative z-10 mt-6 text-xl md:text-2xl font-semibold tracking-tight">
                                            {detail.title}
                                        </h3>
                                        <p className="relative z-10 mt-3 text-sm text-white/55 leading-relaxed">
                                            {detail.tagline}
                                        </p>
                                        <span className="relative z-10 mt-6 inline-flex items-center gap-1.5 text-xs font-medium text-white/70 group-hover:text-white">
                                            {u.viewService}
                                            <ArrowUpRight className="h-3 w-3" />
                                        </span>
                                    </Link>
                                </motion.div>
                            );
                        })}
                    </div>
                </section>
            </main>
            <Footer />
        </div>
    );
}
